import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { resolveImage, resolvePrivateImage, formatRM, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, ExternalLink, MessageCircle, CreditCard } from "lucide-react";

export default function OrderDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState({});
  const [uploading, setUploading] = useState(false);
  const [payCfg, setPayCfg] = useState({ enabled: false });
  const [payingStripe, setPayingStripe] = useState(false);

  const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(null));

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/payments/config").then((r) => setPayCfg(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const retryStripe = async () => {
    setPayingStripe(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "order", ref_id: order.id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err)); setPayingStripe(false); }
  };

  if (!order) {
    return <div className="mx-auto max-w-3xl px-6 lg:px-10 py-16 text-[#4a6e77]">{t("common.loading")}</div>;
  }

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/orders/${order.id}/proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("order.proofUploaded"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const waLink = () => {
    const num = (settings.whatsapp_number || "").replace(/[^\d]/g, "");
    if (!num) return null;
    const msg = zh
      ? `您好，我的订单 #${order.order_no}，金额 ${formatRM(order.total)}。`
      : `Hello, my order #${order.order_no}, total ${formatRM(order.total)}.`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-16" data-testid="order-detail-page">
      <Link to="/dashboard" className="text-sm text-[#4a6e77] hover:text-[#005870]" data-testid="back-to-dashboard">
        ← {t("common.back")}
      </Link>
      <h1 className="mt-3 font-serif-cn text-3xl md:text-4xl text-[#003645] font-semibold">
        {t("order.title")} #{order.order_no}
      </h1>
      <div className="mt-2 text-sm text-[#4a6e77]">
        {new Date(order.created_at).toLocaleString()}
      </div>

      {/* Status */}
      <div className="mt-6 flex items-center flex-wrap gap-3">
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
            order.status === "confirmed"
              ? "bg-[#00868e]/10 text-[#00868e]"
              : order.status === "rejected"
                ? "bg-red-50 text-red-700"
                : "bg-amber-50 text-amber-700"
          }`}
          data-testid="order-status-badge"
        >
          {t(`order.statuses.${order.status}`)}
        </span>
        {waLink() && (
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-[#25D366] hover:opacity-80"
            data-testid="order-wa-btn"
          >
            <MessageCircle size={14} /> {t("order.contactWa")}
          </a>
        )}
      </div>

      {order.status === "rejected" && order.reject_reason && (
        <div
          className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          data-testid="reject-reason-notice"
        >
          <div className="font-medium">{t("order.rejectReason")}</div>
          <div className="mt-1">{order.reject_reason}</div>
        </div>
      )}
      {order.status === "confirmed" && (
        <div className="mt-4 rounded-md border border-[#00868e]/30 bg-[#00868e]/8 px-4 py-3 text-sm text-[#003645]" style={{ background: "rgba(0,134,142,0.06)" }}>
          {t("order.confirmed")}
        </div>
      )}

      {/* Items */}
      <div className="mt-8 rounded-lg border border-[#e5ebeb] bg-white p-6">
        <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">items</div>
        <ul className="mt-4 divide-y divide-[#e5ebeb]">
          {(order.items || []).map((it) => (
            <li key={it.product_id} className="py-3 flex items-center gap-4">
              <div className="h-14 w-14 rounded overflow-hidden bg-[#f5f0e8] shrink-0">
                {it.image && <img src={resolveImage(it.image)} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif-cn text-[#003645] truncate">
                  {zh ? it.name_zh : it.name_en}
                </div>
                <div className="text-xs text-[#4a6e77]">× {it.quantity}</div>
              </div>
              <div className="text-[#003645] font-medium">{formatRM(it.subtotal)}</div>
            </li>
          ))}
          {(order.events || []).map((ev) => (
            <li key={"e-" + ev.event_id} className="py-3 flex items-center gap-4" data-testid={`order-event-${ev.event_id}`}>
              <div className="h-14 w-14 rounded overflow-hidden bg-[#f5f0e8] shrink-0 flex items-center justify-center">
                {ev.image ? <img src={resolveImage(ev.image)} alt="" className="h-full w-full object-cover" /> : <span className="text-[10px] text-[#00868e]">EVENT</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#00868e] font-semibold">
                  {t("cartTypes.event")}
                </div>
                <div className="font-serif-cn text-[#003645] truncate">
                  {zh ? ev.title_zh : ev.title_en}
                </div>
                <div className="text-xs text-[#4a6e77]">× {ev.quantity}{ev.starts_at ? ` · ${new Date(ev.starts_at).toLocaleString()}` : ""}</div>
              </div>
              <div className="text-[#003645] font-medium">{formatRM(ev.subtotal)}</div>
            </li>
          ))}
        </ul>
        <div className="mt-4 pt-4 border-t border-[#e5ebeb] space-y-1 text-sm">
          {(order.product_subtotal || 0) > 0 && (order.events_subtotal || 0) > 0 && (
            <>
              <div className="flex justify-between text-[#4a6e77]" data-testid="order-product-subtotal"><span>{t("cartTypes.product")}</span><span>{formatRM(order.product_subtotal)}</span></div>
              <div className="flex justify-between text-[#4a6e77]" data-testid="order-events-subtotal"><span>{t("cartTypes.event")}</span><span>{formatRM(order.events_subtotal)}</span></div>
            </>
          )}
          <div className="flex justify-between text-[#4a6e77]">
            <span>{t("common.subtotal")}</span>
            <span>{formatRM(order.total)}</span>
          </div>
          {(order.points_used || 0) > 0 && (
            <div className="flex justify-between text-[#00868e]">
              <span>- {t("points.title")}</span>
              <span>- {formatRM(order.points_used)}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold pt-1 text-base">
            <span className="text-[#003645]">{t("checkout.amount")}</span>
            <span className="text-[#005870] text-lg" data-testid="order-cash-paid">{formatRM(order.cash_paid ?? order.total)}</span>
          </div>
        </div>
      </div>

      {/* Shipping */}
      <div className="mt-6 rounded-lg border border-[#e5ebeb] bg-white p-6">
        <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">shipping</div>
        <div className="mt-3 text-sm text-[#003645] leading-relaxed">
          <div>{order.shipping_name} · {order.shipping_phone}</div>
          <div className="text-[#4a6e77] mt-1">{order.shipping_address}</div>
          {order.notes && <div className="mt-2 text-[#4a6e77]">📝 {order.notes}</div>}
        </div>
      </div>

      {/* Bank + Proof (hidden if Stripe-paid) */}
      {order.payment_method === "stripe" && order.stripe_payment_status === "paid" ? (
        <div className="mt-6 rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="order-stripe-paid-block">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">payment</div>
          <div className="mt-3 inline-flex items-center gap-1.5 text-[#00868e] font-medium">
            ✓ {i18n.language?.startsWith("en") ? "Paid via Stripe" : "已通过 Stripe 支付"}
          </div>
        </div>
      ) : (
      <div className="mt-6 rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="proof-section">
        {order.payment_method === "stripe" && order.stripe_payment_status !== "paid" && order.status !== "confirmed" && order.status !== "rejected" && payCfg.enabled && (
          <div className="mb-6 pb-6 border-b border-[#e5ebeb]" data-testid="stripe-retry-block">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
              {i18n.language?.startsWith("en") ? "resume online payment" : "继续在线支付"}
            </div>
            <p className="mt-2 text-sm text-[#4a6e77]">
              {i18n.language?.startsWith("en")
                ? "You started Stripe checkout but did not complete it. Retry the card payment or switch to bank transfer below."
                : "您上次选择了 Stripe 在线支付但未完成。可重试卡支付，或改用下方银行转账。"}
            </p>
            <button
              type="button"
              onClick={retryStripe}
              disabled={payingStripe}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645] disabled:opacity-60"
              data-testid="order-retry-stripe-btn"
            >
              {payingStripe ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
              {i18n.language?.startsWith("en") ? "Retry online payment" : "重试在线支付"}
            </button>
          </div>
        )}
        <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
          payment proof
        </div>
        <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
          <div><div className="text-[#4a6e77]">{t("checkout.bankName")}</div><div className="text-[#003645] font-medium">{settings.bank_name}</div></div>
          <div><div className="text-[#4a6e77]">{t("checkout.accountName")}</div><div className="text-[#003645] font-medium">{settings.bank_account_name}</div></div>
          <div><div className="text-[#4a6e77]">{t("checkout.accountNumber")}</div><div className="text-[#003645] font-medium tracking-wider">{settings.bank_account_number}</div></div>
        </div>

        {order.payment_proof ? (
          <div className="mt-6">
            <div className="text-sm text-[#4a6e77] mb-2">{t("order.proofUploaded")}</div>
            <a
              href={resolvePrivateImage(order.payment_proof)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
              data-testid="view-proof-link"
            >
              <img
                src={resolvePrivateImage(order.payment_proof)}
                alt="proof"
                className="max-h-64 rounded-md border border-[#e5ebeb]"
              />
            </a>
            <div className="mt-3">
              <label className="inline-flex items-center gap-2 text-sm text-[#005870] hover:text-[#00868e] cursor-pointer">
                <Upload size={14} /> {t("order.reuploadProof")}
                <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="reupload-proof-input" />
              </label>
            </div>
          </div>
        ) : (
          <div className="mt-6">
            <label
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645] cursor-pointer transition-colors duration-200"
              data-testid="upload-proof-btn"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {t("order.uploadProof")}
              <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} data-testid="upload-proof-input" />
            </label>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
